import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { stripeService } from '../../services/stripeService';
import SiteProofLogo from '../../components/SiteProofLogo';

const MAX_SYNC_ATTEMPTS = 12;
const SYNC_INTERVAL_MS = 1500;

const CheckoutSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [statusMessage, setStatusMessage] = useState('Setting up your account…');
  const [error, setError] = useState(null);
  const [dots, setDots] = useState('');
  const ranRef = useRef(false);

  // Animated dots for loading state
  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => (prev?.length >= 3 ? '' : prev + '.'));
    }, 500);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (ranRef?.current) return;
    ranRef.current = true;

    const run = async () => {
      try {
        // ── Step 1: Ensure auth session is present ──────────────────────────
        setStatusMessage('Verifying your session');

        let session = null;
        for (let i = 0; i < 10; i++) {
          const { data } = await supabase?.auth?.getSession();
          if (data?.session?.user?.id) {
            session = data?.session;
            break;
          }
          await new Promise(r => setTimeout(r, 300));
        }

        if (!session?.user?.id) {
          // No session — redirect to login with a return hint
          navigate('/login?redirect=/admin-dashboard', { replace: true });
          return;
        }

        const userId = session?.user?.id;

        // ── Step 2: Get company ID ──────────────────────────────────────────
        setStatusMessage('Loading your account');

        const { data: profile } = await supabase
          ?.from('user_profiles')
          ?.select('company_id, role')
          ?.eq('id', userId)
          ?.maybeSingle();

        const companyId = profile?.company_id;

        if (!companyId) {
          // No company yet — go to setup
          navigate('/setup-flow-company-details', { replace: true });
          return;
        }

        // ── Step 3: Mark onboarding flags (no status write — let the webhook own that) ─
        setStatusMessage('Activating your subscription');

        await supabase
          ?.from('companies')
          ?.update({
            setup_complete: true,
            onboarding_finished: true,
          })
          ?.eq('id', companyId);

        // ── Step 4: Sync from Stripe then poll until webhook has written back ─
        setStatusMessage('Syncing your plan details');

        // Kick off a sync immediately (attempt 0) so we don't wait for the webhook
        // to arrive — especially important for payment links which carry no metadata.
        try { await stripeService?.syncSubscription(companyId); } catch (_) {}

        // Poll until the DB shows a stripe_subscription_id (written by webhook or
        // sync-subscription) AND a paid status. Checking stripe_subscription_id
        // ensures we are not fooled by a stale optimistic write.
        let synced = false;
        for (let attempt = 0; attempt < MAX_SYNC_ATTEMPTS; attempt++) {
          try {
            const { data: company } = await supabase
              ?.from('companies')
              ?.select('subscription_status, stripe_subscription_id, setup_complete')
              ?.eq('id', companyId)
              ?.maybeSingle();

            const status = company?.subscription_status;
            const hasSub = Boolean(company?.stripe_subscription_id);
            if (hasSub && (status === 'active' || status === 'trialing')) {
              synced = true;
              break;
            }

            // Retry sync on attempt 4 in case the first call lost the race
            if (attempt === 4) {
              try { await stripeService?.syncSubscription(companyId); } catch (_) {}
            }
          } catch (_) {}

          if (attempt < MAX_SYNC_ATTEMPTS - 1) {
            await new Promise(r => setTimeout(r, SYNC_INTERVAL_MS));
          }
        }

        if (!synced) {
          // Polling exhausted — try one final server-side sync rather than
          // writing trialing client-side (which would be a bypass vector).
          console.warn('[CheckoutSuccess] Sync polling exhausted — running final server-side sync');
          try {
            await stripeService?.syncSubscription(companyId);
          } catch (_) {}
        }

        // ── Step 5: Send trial welcome email (fire-and-forget) ─────────────
        try {
          const { data: company } = await supabase
            ?.from('companies')
            ?.select('company_name, max_active_sites, trial_end_date, current_period_end')
            ?.eq('id', companyId)
            ?.maybeSingle();

          if (company && session?.user?.email) {
            const planName = `${company?.max_active_sites || 'your'} active sites`;
            const trialEnd = company?.trial_end_date || company?.current_period_end;
            const formatDate = (d) => d ? new Date(d)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : null;
            const trialEndFormatted = formatDate(trialEnd) || 'your trial end date';

            const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
            const anonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

            fetch(`${supabaseUrl}/functions/v1/send-email-notifications`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session?.access_token}`,
                'apikey': anonKey,
              },
              body: JSON.stringify({
                type: 'trial_welcome',
                toEmail: session?.user?.email,
                recipientName: session?.user?.user_metadata?.full_name || session?.user?.email,
                companyName: company?.company_name || '',
                planName,
                trialEndDate: trialEndFormatted,
                chargeDate: trialEndFormatted,
              }),
            })?.catch(() => {});
          }
        } catch (_) {}

        // ── Step 6: Clear AuthRouter cache and navigate ─────────────────────
        setStatusMessage('Taking you to your dashboard');

        // Small delay so the "Taking you to your dashboard" message is visible
        await new Promise(r => setTimeout(r, 600));

        // Use window.location for a clean navigation that resets all React state
        // including the AuthRouter module-level cache (_resolvedCache)
        window.location?.replace('/admin-dashboard');
      } catch (err) {
        console.error('[CheckoutSuccess] Unexpected error:', err);
        setError('Something went wrong setting up your account. Please try refreshing the page or contact support.');
      }
    };

    run();
  }, [navigate]);

  if (error) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="mb-6 flex justify-center">
            <SiteProofLogo iconOnly />
          </div>
          <div className="bg-card border border-border rounded-2xl p-8 shadow-elevation-2">
            <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-lg font-bold text-foreground mb-2">Setup issue</h2>
            <p className="text-sm text-muted-foreground mb-6">{error}</p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => window.location?.replace('/admin-dashboard')}
                className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-semibold py-3 px-4 rounded-xl transition-colors text-sm"
              >
                Go to Dashboard
              </button>
              <button
                onClick={() => window.location?.reload()}
                className="w-full bg-muted hover:bg-muted/80 text-foreground font-medium py-3 px-4 rounded-xl transition-colors text-sm"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4">
      <div className="max-w-sm w-full text-center">
        <div className="mb-8 flex justify-center">
          <SiteProofLogo iconOnly />
        </div>

        <div className="bg-card border border-border rounded-2xl p-8 shadow-elevation-2">
          {/* Success checkmark */}
          <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-5">
            <svg className="w-8 h-8 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>

          <h1 className="text-xl font-bold text-foreground mb-2">Payment successful!</h1>
          <p className="text-sm text-muted-foreground mb-6">
            Your trial has started. We're setting up your account now.
          </p>

          {/* Loading indicator */}
          <div className="flex items-center justify-center gap-3 py-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-accent flex-shrink-0"></div>
            <span className="text-sm text-muted-foreground">
              {statusMessage}{dots}
            </span>
          </div>
        </div>

        <p className="mt-4 text-xs text-muted-foreground">
          This usually takes just a few seconds.
        </p>
      </div>
    </div>
  );
};

export default CheckoutSuccess;
